import { toCents, toNet, toNumber } from "../../shared/money";
import { itemTax, quoteRate } from "./data";
import type { MaterialRow, PriceListData, PricingRule, TaxQuote } from "./meta";
import { asGroupTaxed, calcPrice, estimatedCost, itemProfit, needsCheck, pricedAs, type Profit, samePrice } from "./pricing";

/*
 * An item's pricing draft (its Pricing tab's, kept by its window) and what follows from it: the item as it has it, the price it
 * gives, and what the item makes now and once updated (ProfitBox, on its Pricing and Costing tabs). Apart from the tab, so the box
 * can use them too.
 */

/** An item's own pricing being edited, not saved yet (kept in its window, like a group's draft). */
export interface ItemDraft {
    /** Its own rule, or null: a typed price. */
    rule: PricingRule | null;
    /** The typed price, as typed (in `tax`'s terms): for a typed price. */
    price: string;
    /** How it's taxed (its rate resolved: the account default when it has none). */
    tax: TaxQuote;
    /** Starting to override its group's price (Override pressed): saving makes it an override, Cancel doesn't. */
    override?: boolean;
}

/** The item as a draft has it: taxed the draft's way (its cost the same before tax), with its typed price. */
export function itemAsDrafted(data: PriceListData, item: MaterialRow, d: ItemDraft, partCost?: string) {
    const { tax } = quoteRate(data, d.tax);
    // Its cost (in its basis): as it is, or (`partCost`) as it'll be once its cost is updated.
    const part = partCost === undefined ? item : { ...item, cost: partCost };
    const taxed = asGroupTaxed(part, itemTax(data, item), { ...d.tax, tax });
    return { item: { ...taxed, price: d.rule ? item.price : d.price }, tax };
}

/**
 * The price a draft gives (in its tax's terms): its rule's, or its typed price. Worked
 * out from its cost as it is, or (`partCost`) as it'll be once its cost is updated.
 */
export function draftPrice(data: PriceListData, item: MaterialRow, d: ItemDraft, partCost?: string) {
    if (!d.rule) return d.price;
    const now = itemAsDrafted(data, item, d, partCost);
    return calcPrice(now.item, now.tax, d.rule)?.price ?? toCents(toNumber(item.price));
}

/** What an item makes in one case (now, or once updated): its price before tax (at `tax`), and its Profit. */
export interface ProfitCase {
    priceNet: number;
    tax: number;
    profit: Profit;
}

/**
 * What the item is estimated to make, before tax, now (ServiceM8's price and cost as they are) and once updated (its new price,
 * taxed as it'll be, on the cost it'll have: pricing.ts itemProfit). Once updated, its price is the one its pricing gives from its
 * cost as it'll be (a cost that moves can move its price: a markup), as updating both at once does
 * (supplierPrices.ts updatePriceAndCost). Being edited: its pricing draft's price, and its cost draft's cost
 * (`costTarget`: its cost as that would make it). `changes`: once updated isn't now (two to show).
 */
export function profitCases(data: PriceListData, item: MaterialRow, draft: ItemDraft | null, costTarget?: string): { now: ProfitCase; next: ProfitCase; changes: boolean } {
    const itemRate = itemTax(data, item);
    const { tax } = itemRate;
    const pricing = data.itemPricing[item.uuid];
    // Its cost being changed (its Costing tab): estimated on the cost that gives.
    if (costTarget !== undefined) {
        const { [item.uuid]: _was, ...others } = data.costUpdates;
        data = { ...data, costUpdates: samePrice(costTarget, item.cost) ? others : { ...others, [item.uuid]: costTarget } };
    }
    const now: ProfitCase = { profit: itemProfit({ ...data, costUpdates: {} }, item, tax), priceNet: toNet(toNumber(item.price), item.price_includes_taxes, tax), tax };
    let next: ProfitCase = { ...now, profit: itemProfit(data, item, tax) };
    let changes = item.uuid in data.costUpdates;
    if (draft) {
        // Being edited: at the price the draft gives (taxed its way), on its cost as estimated.
        const cost = toCents(toNumber(estimatedCost(data, item)));
        const after = itemAsDrafted(data, item, draft, cost);
        const price = draftPrice(data, item, draft, cost);
        next = { profit: itemProfit(data, { ...after.item, price }, after.tax, { was: item }), priceNet: toNet(toNumber(price), after.item.price_includes_taxes, after.tax), tax: after.tax };
        changes = !samePrice(price, item.price) || item.uuid in data.costUpdates || after.item.price_includes_taxes !== item.price_includes_taxes;
    } else {
        // From its cost as it'll be (its cost to update's, or its cost draft's).
        const check = needsCheck({ ...item, cost: estimatedCost(data, item) }, itemRate, pricing);
        changes ||= check !== null;
        if (check?.taxTo) {
            const after = pricedAs(item, itemRate, pricing);
            next = { ...next, profit: itemProfit(data, { ...after.item, price: check.target }, after.tax, { was: item }), priceNet: toNet(toNumber(check.target), after.item.price_includes_taxes, after.tax), tax: after.tax };
        } else if (check) {
            next = { ...next, profit: itemProfit(data, { ...item, price: check.target }, tax), priceNet: toNet(toNumber(check.target), item.price_includes_taxes, tax) };
        }
    }
    return { now, next, changes };
}
